
import './synchro-modal.scss'

import { defineComponent } from 'vue'
import { TranslateResult } from 'vue-i18n'
import { tap } from 'rxjs/operators'
import { DateTime } from 'luxon'
import { faCogs, faBoxOpen, faCheck, faCloudDownloadAlt, faCloudUploadAlt, faEdit, faExclamationTriangle, faInfoCircle, faPlusCircle, faSlash, faSyncAlt, faTabletAlt, faTimes, faToggleOff, faToggleOn, faWifi } from '@fortawesome/free-solid-svg-icons'

import { DataSynchronisationStatus, DataSynchronizerStats, EntityType } from '@skinsoft/types'
import { SynchroDownHandler, SynchroUpHandler } from "@skinsoft/classes-interfaces-definition";
import { formatDateFromNow, formatDuration } from '@skinsoft/utils'
import { OfflineDataFetcher, OfflineDataUpdateCacher } from '@skinsoft/offline'
import { ImageTransferHelper } from '@skinsoft/media-transfer-helper'
import {
    dataSynchroEventBus$,
    AliasSyncDownHandler,
    AuditCampaignSyncDownHandler,
    AuditRecordSyncDownHandler,
    AuthoritySyncDownHandler,
    ConditionReportSyncDownHandler,
    EntityFieldSyncDownHandler,
    LocationSyncDownHandler,
    ThesaurusSyncDownHandler,
    ThesaurusTermSyncDownHandler,
    WemiSynchroDownHandler,
    ArchiveSyncDownHandler,
    SpecimenSyncDownHandler
} from '@skinsoft/synchro-down-handlers'
import {
    ArchiveSyncUpHandler,
    AuditCampaignSyncUpHandler,
    AuditRecordSyncUpHandler,
    ConditionReportSyncUpHandler,
    WemiSyncUpHandler,
    SpecimenSyncUpHandler
} from '@skinsoft/synchro-up-handlers'
import { SynchroUpStrategy } from '@skinsoft/synchro-up-strategy'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus'
import { OnlineStatusIndicator } from '../online-status-indicator'
import { SynchroView } from '../synchro-view'

interface SynchDownHandlerWrapper {
    title: string,
    synchDownHandler: SynchroDownHandler,
}

interface SynchUpHandlerWrapper {
    title: string,
    synchUpHandler: SynchroUpHandler,
}

interface SynchroUpHandlerStats {
    total: number
    nbCreations: number
    nbUpdates: number
}

declare const config

declare const SYNCH_UP_HANDLERS: { title: string, synchUpHandler: string, filterWorkspaces?: string[] }[]

declare const SYNCH_DOWN_HANDLERS: {title: string, synchDownHandler: string, filterWorkspaces?: string[]}[]

const CLEAN_GOHST_IMAGE_BEFORE_SYNC_DOWN: boolean = config.gohstImageCleaningSettings.beforeSyncDown
const CLEAN_GOHST_IMAGE_AFTER_SYNC_UP: boolean = config.gohstImageCleaningSettings.afterSyncUp
const RUN_SYNC_DOWN_PARALLEL: boolean = config.syncDownParallel

export const SynchroModal = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sOnlineStatusIndicator: OnlineStatusIndicator,
        sSynchroView: SynchroView
    },
    props: [''],
    template: require('./synchro-modal.html'),
    data() {
        return {
            // Synchro
            synchDownHandlerWrappers: [] as SynchDownHandlerWrapper[],
            synchUpHandlersWrappers: [] as SynchUpHandlerWrapper[],
            nbSynchroCompleted: 0,
            errorInGlobalSynchro: false,
            progressBarVariant: "success",
            globalSyncing: false,
            executionStartTime: null as DateTime | null,
            lastSynchroStats: null as any as DataSynchronizerStats,
            showDownloadDetails: false,

            // Push
            pushResults: [] as any[],
            nbEntitiesInUpdateCache: 0,
            pushAllStatus: DataSynchronisationStatus.SUCCESS,
            nbPushesCompleted: 0,
            nbSuccesfullPushes: 0,
            lastPushStats: { date: 0, status: DataSynchronisationStatus.SUCCESS } as any,
            globalPushing: false,
            showUploadDetails: false,
            pushProgressBarVariant: "success",
            synchroUpHandlersStats: [] as SynchroUpHandlerStats[],

            showClearCacheDownDialog: false,
            showClearCacheUpDialog: false,

            // Icons
            syncIcon: faSyncAlt,
            crossIcon: faTimes,
            wifiIcon: faWifi,
            slashIcon: faSlash,
            downloadIcon: faCloudDownloadAlt,
            uploadIcon: faCloudUploadAlt,
            infoIcon: faInfoCircle,
            warningIcon: faExclamationTriangle,
            localIcon: faTabletAlt,
            switchOnIcon: faToggleOn,
            switchOffIcon: faToggleOff,
            checkIcon: faCheck,
            statusErrorIcon: faTimes,
            emptyIcon: faBoxOpen,
            editIcon: faEdit,
            plusIcon: faPlusCircle,
            menuIcon: faCogs,
        }
    },
    mounted() {
        this.fetchLastSynchroStats()
        this.getNbEntitiesInUpdateCache()
        this.initSyncUpHandlerStats()
        dataSynchroEventBus$.subscribe(
            async event => {
                if (event.status === DataSynchronisationStatus.SUCCESS) {
                    this.nbSynchroCompleted++;
                    // All sychros are completed
                    if (this.nbSynchroCompleted === this.nbSubSynchros) {
                        await this.saveGlobalStats(DataSynchronisationStatus.SUCCESS)
                        this.globalSyncing = false;
                    }
                } else if (event.status === DataSynchronisationStatus.ERROR) {
                    this.errorInGlobalSynchro = true;
                    this.progressBarVariant = "danger";
                    await this.saveGlobalStats(event.status)
                    this.globalSyncing = false;
                }
            }
        )
    },
    computed: {
        lastSynchroDuration(): string {
            return formatDuration(
                this.lastSynchroStats?.lastSynchroDuration,
                this.$t("time.minutes"),
                this.$t("time.seconds")
            ) || "..."
        },
        lastSynchroDate(): string {
            const formattedDate = formatDateFromNow(this.lastSynchroStats?.lastSynchroDate, this.getAppLocale())
            return formattedDate !== "" ? formattedDate || "..." : "...";
        },
        lastSynchroStatus(): TranslateResult {
            return this.lastSynchroStats?.lastSynchroStatus ?
                this.$t(`state.${this.lastSynchroStats.lastSynchroStatus}`) :
                "...";
        },
        lastPushStatus(): TranslateResult {
            return this.lastPushStats?.status ?
                this.$t(`state.${this.lastPushStats.status}`) :
                "...";
        },
        formattedLastPushDate(): string {
            const formattedDate = formatDateFromNow(this.lastPushStats?.date, this.getAppLocale())
            return formattedDate || "...";
        },
        nbSubSynchros(): number {
            return this.synchDownHandlerWrappers.length
        },
        nbDataPushers(): number {
            return this.synchUpHandlersWrappers.length;
        },
    },
    methods: {
        setupSynchroDownHandlers() {
            if(SYNCH_DOWN_HANDLERS) {
                const workspaceType = localStorage.getItem("workspaceType")
                let synchroHandlersWrappers: (SynchDownHandlerWrapper | null)[] = []

                synchroHandlersWrappers = SYNCH_DOWN_HANDLERS.map(config => {
                    let synchroDownHandler: SynchroDownHandler

                    switch(config.synchDownHandler) {
                        case "WemiSynchroDownHandler":
                            synchroDownHandler = new WemiSynchroDownHandler()
                            break;
                        case "ConditionReportSyncDownHandler":
                            synchroDownHandler = ConditionReportSyncDownHandler.getInstance();
                            break;
                        case "ArchiveSyncDownHandler":
                            synchroDownHandler = ArchiveSyncDownHandler.getInstance()
                            break
                        case "AuditRecordSyncDownHandler":
                            synchroDownHandler = AuditRecordSyncDownHandler.getInstance();
                            break;
                        case "AuditCampaignSyncDownHandler":
                            synchroDownHandler = AuditCampaignSyncDownHandler.getInstance();
                            break;
                        case "ThesaurusTermSyncDownHandler":
                            synchroDownHandler = ThesaurusTermSyncDownHandler.getInstance();
                            break;
                        case "ThesaurusSyncDownHandler":
                            synchroDownHandler = ThesaurusSyncDownHandler.getInstance();
                            break;
                        case "EntityFieldSyncDownHandler":
                            synchroDownHandler = EntityFieldSyncDownHandler.getInstance();
                            break;
                        case "AliasSyncDownHandler":
                            synchroDownHandler = AliasSyncDownHandler.getInstance();
                            break;
                        case "AuthoritySyncDownHandler":
                            synchroDownHandler = AuthoritySyncDownHandler.getInstance();
                            break;
                        case "LocationSyncDownHandler":
                            synchroDownHandler = LocationSyncDownHandler.getInstance();
                            break;
                        case "SpecimenSyncDownHandler":
                            synchroDownHandler = SpecimenSyncDownHandler.getInstance();
                            break;
                        default:
                            return null;
                    }

                    if(config.filterWorkspaces?.length) {
                        if(!config.filterWorkspaces.includes(workspaceType ?? "")) {
                            return null
                        }
                    }
                    return {
                        title: config.title,
                        synchDownHandler: synchroDownHandler,
                    }
                })

                this.synchDownHandlerWrappers = synchroHandlersWrappers.filter(wrapper => {
                    return wrapper != null
                }) as SynchDownHandlerWrapper[]
            }
        },

        /*
         * sets this.dataStores with the configuration defined in vue.config.js
        */
        setupSynchroUpHandlers() {
            if(SYNCH_UP_HANDLERS) {
                const workspaceType = localStorage.getItem("workspaceType")
                let wrappers: Array<SynchUpHandlerWrapper | null>;

                wrappers = SYNCH_UP_HANDLERS.map(config => {
                    // @ts-ignore
                    let synchUpHandler: SynchroUpHandler

                    switch(config.synchUpHandler) {
                        case "ArchiveSyncUpHandler":
                            synchUpHandler = ArchiveSyncUpHandler.getInstance()
                            break
                        case "ConditionReportSyncUpHandler":
                            synchUpHandler = ConditionReportSyncUpHandler.getInstance();
                            break;
                        case "WemiSyncUpHandler":
                            synchUpHandler = WemiSyncUpHandler.getInstance();
                            break;
                        case "AuditRecordSyncUpHandler":
                            synchUpHandler = AuditRecordSyncUpHandler.getInstance();
                            break;
                        case "AuditCampaignSyncUpHandler":
                            synchUpHandler = AuditCampaignSyncUpHandler.getInstance();
                            break;
                        case "SpecimenSyncUpHandler":
                            synchUpHandler = SpecimenSyncUpHandler.getInstance()
                            break
                        default:
                            return null;
                    }

                    if(config.filterWorkspaces?.length) {
                        if(!config.filterWorkspaces.includes(workspaceType ?? "")) {
                            return null
                        }
                    }
                    return { title: config.title, synchUpHandler: synchUpHandler }
                })

                this.synchUpHandlersWrappers = wrappers.filter(wrapper => {
                    return wrapper != null
                }) as SynchUpHandlerWrapper[]
            }
        },

        open() {
            this.setupSynchroDownHandlers()
            this.setupSynchroUpHandlers()
            ;(this.$refs.modal as any).show()
            this.getNbEntitiesInUpdateCache()
            // Need to refresh time-based properties
            this.$forceUpdate();
        },

        // Fetching data
        async syncAll() {
            if(CLEAN_GOHST_IMAGE_BEFORE_SYNC_DOWN) {
                await this.$imageTransferHelper.cleanCachedImages()
            }

            this.executionStartTime = DateTime.now();
            this.globalSyncing = true;
            this.nbSynchroCompleted = 0;
            this.progressBarVariant = "success";
            this.errorInGlobalSynchro = false;
            for (const synchDownHandlerWrapper of this.synchDownHandlerWrappers) {
                if(RUN_SYNC_DOWN_PARALLEL) {
                    synchDownHandlerWrapper.synchDownHandler.synchronize(0, true, true)
                } else {
                    await synchDownHandlerWrapper.synchDownHandler.synchronize(0, true, true)
                }
            }
        },

        async abortAll() {
            await this.saveGlobalStats(DataSynchronisationStatus.ABORTED)
            this.progressBarVariant = "danger";
            for (const synchDownWrapper of this.synchDownHandlerWrappers) {
                synchDownWrapper.synchDownHandler.abortSynchronisation()
            }
            this.globalSyncing = false;
        },

        async saveGlobalStats(status: DataSynchronisationStatus) {
            // Saving global sync stats to cache
            await OfflineDataFetcher.saveGlobalSyncStats({
                currentNbSychronized: 0,
                lastSynchroDate: DateTime.now().toMillis(),
                lastSynchroDuration: this.executionStartTime ? DateTime.now().diff(this.executionStartTime).toMillis() : 0,
                lastSynchroStatus: status,
                maxNbToSynchronize: 0,
            })
            this.fetchLastSynchroStats()
        },

        /**
         * Retrieve last synchro and push stats from cache
         */
        async fetchLastSynchroStats() {
            this.lastSynchroStats = await OfflineDataFetcher.getLastGlobalSyncStats() as any
            this.lastPushStats = await OfflineDataUpdateCacher.getLastPushStats()
        },

        statusIcon(status: DataSynchronisationStatus) {
            if (status) {
                if (status === DataSynchronisationStatus.SUCCESS) {
                    return faCheck;
                } else {
                    return faTimes;
                }
            }
            return null;
        },

        statusIconDataCy(status: DataSynchronisationStatus) {
            if (status) {
                if (status === DataSynchronisationStatus.SUCCESS) {
                    return "synchro-modal--sync-up--sucess-icon"
                } else {
                    return "synchro-modal--sync-up--failure-icon"
                }
            }
            return ""
        },

        statusIndicatorStyle(status: DataSynchronisationStatus) {
            if (status) {
                if (status === DataSynchronisationStatus.SUCCESS) {
                    return "status-success";
                } else {
                    return "status-error";
                }
            }
            return "";
        },

        async getNbEntitiesInUpdateCache() {
            this.nbEntitiesInUpdateCache = 0;
            for (const [index, syncUpHandlerWrapper] of this.synchUpHandlersWrappers.entries()) {
                const nbCachedEntities = await syncUpHandlerWrapper.synchUpHandler.getNbCachedObjects();
                this.nbEntitiesInUpdateCache += nbCachedEntities

                const syncUpHandlerStats = await this.getSynchroUpHandlerStats(syncUpHandlerWrapper.synchUpHandler)

                this.synchroUpHandlersStats.splice(
                    index,
                    1,
                    syncUpHandlerStats
                )
            }
        },

        async initSyncUpHandlerStats() {
            for(const wrapper of this.synchUpHandlersWrappers) {
                const stats = await this.getSynchroUpHandlerStats(wrapper.synchUpHandler)

                this.synchroUpHandlersStats.push(stats)
            }
        },

        async getSynchroUpHandlerStats(synchroUpHandler: SynchroUpHandler): Promise<SynchroUpHandlerStats> {
            const totalEntity = await synchroUpHandler.getNbCachedObjects()
            const nbCreations = await synchroUpHandler.getNbLocallyCreatedEntities()

            return {
                total: totalEntity,
                nbCreations: nbCreations,
                nbUpdates: totalEntity - nbCreations,
            }
        },

        // Uploading data
        async pushAll() {
            this.nbSuccesfullPushes = 0;
            this.nbPushesCompleted = 0;
            let totalNbInCache = 0;

            this.globalPushing = true;
            this.pushResults = [];

            const allSyncUpHandler = this.synchUpHandlersWrappers.map(wrapper => {
                return wrapper.synchUpHandler
            })

            for (const synchUpHandler of allSyncUpHandler) {
                totalNbInCache += await synchUpHandler.getNbCachedObjects()
            }

            const entitySyncedSubject = await new SynchroUpStrategy(allSyncUpHandler).run()

            entitySyncedSubject.subscribe(result => {
                if(result.entityType !== EntityType.MEDIA) {
                    this.onDataStorePushCompleted(result.success, totalNbInCache)
                }
            })
        },

        async onDataStorePushCompleted(updateSuccess: boolean, totalNbInCache: number) {
            this.nbPushesCompleted++;
            if (updateSuccess) {
                this.nbSuccesfullPushes++;
            }

            // All updates have been performed (with success or not)
            if (this.nbPushesCompleted === totalNbInCache) {
                if (this.nbPushesCompleted !== this.nbSuccesfullPushes) {
                    this.pushAllStatus = DataSynchronisationStatus.ERROR;
                    this.pushProgressBarVariant = "danger";
                } else {
                    this.pushAllStatus = DataSynchronisationStatus.SUCCESS;
                    this.pushProgressBarVariant = "sucess";
                }
                this.getNbEntitiesInUpdateCache()
                // Saving push stats
                const now = DateTime.now().toMillis()
                await OfflineDataUpdateCacher.setLastPushStats(now, this.pushAllStatus);
                this.fetchLastSynchroStats()
                this.globalPushing = false;

                if(CLEAN_GOHST_IMAGE_AFTER_SYNC_UP) {
                    await this.$imageTransferHelper.cleanCachedImages()
                }
            }
        },

        abortPushAll() {
            this.globalPushing = false;
            for (const wrapper of this.synchUpHandlersWrappers) {
                wrapper.synchUpHandler.abortPush();
            }
        },

        async onClearImageCacheClicked() {
            await this.$imageTransferHelper.cleanCachedImages()
            this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                message: this.$t('synchroModal.cachedImagedCleaned'),
                variant: "info"
            });
        },

        toggleClearCacheDownDialog() {
            this.showClearCacheDownDialog = !this.showClearCacheDownDialog
        },

        toggleClearCacheUpDialog() {
            this.showClearCacheUpDialog = !this.showClearCacheUpDialog
        },

        async clearCacheDown() {
            for (const synchDownWrapper of this.synchDownHandlerWrappers) {
                await synchDownWrapper.synchDownHandler.clearCacheDown()
            }
            //await InstitutionDataStore.getInstance().clearCacheDown()
            this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                message: this.$t('synchroModal.cacheDownCleaned'),
                variant: 'warning',
            })
        },

        async clearCacheUp() {
            await OfflineDataUpdateCacher.clear()
            this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                message: this.$t('synchroModal.cacheUpCleaned'),
                variant: 'warning',
            })
        },
    },
    watch: {
        globalSyncing() {
            if (!this.globalSyncing) {
                this.$emit("sync-finished");
            }
        },
        globalPushing() {
            if (!this.globalPushing) {
                this.$emit("sync-finished");
                this.emitOnBus('push-synchro-performed')
            }
        },
    },
})
